import { lazy, Suspense } from 'react';
import 'react-phone-number-input/style.css';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import TranslateProvider from './Components/TranslateProvider/TranslateProvider';
import TranslateBanner from './Components/TranslateBanner/TranslateBanner';
import SkeletonLoader from './Components/SkeletonLoader/SkeletonLoader';
import Toast from './Components/Toast/Toast';
import DownloadAppSticky from './Components/DownloadAppSticky/DownloadAppSticky';
import UserLayout from './Layout/UserLayout';
import PublicRoutes from './Router/PublicRoutes';
import Home from './Screens/User/Home/Home';
import useUserStore from './Stores/UserStore';
import HelpCenter from './Screens/User/HelpCenter/HelpCenter';
import InteractWithHappiness from './Screens/User/InteractWithHappiness/InteractWithHappiness';
import UserProfile from './Screens/User/InteractWithHappiness/UserProfile';
import BlockedUsers from './Screens/User/BlockedUsers/BlockedUsers';
import Likes from './Screens/User/Likes/Likes';
import SubscriptionPlans from './Screens/User/SubscriptionPlans/SubscriptionPlans';
import SubscriptionLogs from './Screens/User/SubscriptionLogs/SubscriptionLogs';
import ReferAFriend from './Screens/User/ReferAFriend/ReferAFriend';
import AffiliateProgram from './Screens/User/AffiliateProgram/AffiliateProgram';
import CoinStore from './Screens/User/CoinStore/CoinStore';
import GiftStore from './Screens/User/GiftStore/GiftStore';
import Chat from './Screens/User/Chat/Chat';
import Reminders from './Screens/User/Reminders/Reminders';
import Settings from './Screens/User/Settings/Settings';
import AffiliateRoutes from './Screens/Affiliate';
import TicTacToe from './Screens/User/Games/TicTacToe';
import EmojiPlay from './Screens/User/Games/EmojiPlay';
import ThisOrThat from './Screens/User/Games/ThisOrThat';
import Games from './Screens/User/Games/Games';
import WatchTogether from './Screens/User/WatchTogether/WatchTogether';
import AutoReply from './Screens/User/AutoReply/AutoReply';
import Faqs from './Screens/User/Faqs/Faqs';
// Lazy loaded components
const UserAuthScreens = lazy(() => import('./Screens/Auth/index'));
const AdminAuthScreens = lazy(() => import('./Screens/Admin/Auth/index'));
const AdminScreens = lazy(() => import('./Screens/Admin/index'));
const AdminProfile = lazy(() => import('./Screens/Profile/AdminProfile'));
const Profile = lazy(() => import('./Screens/User/Profile/Profile'));
const EditProfile = lazy(() => import('./Screens/User/Profile/EditProfile'));
const ChangePassword = lazy(() => import('./Screens/User/Profile/ChangePassword'));
const Notifications = lazy(() => import('./Screens/User/Notifications/Notifications'));
const Preferences = lazy(() => import('./Screens/Theme/Preferences'));
const ContactUs = lazy(() => import('./Screens/User/ContactUs/ContactUs'));
const AboutUs = lazy(() => import('./Screens/User/AboutUs/AboutUs'));
const HowToUse = lazy(() => import('./Screens/User/HowToUse/HowToUse'));
const PrivacyPolicy = lazy(() => import('./Screens/User/PrivacyPolicy/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./Screens/User/TermsAndConditions/TermsAndConditions'));
const ShippingPolicy = lazy(() => import('./Screens/User/ShippingPolicy/ShippingPolicy'));
const RefundPolicy = lazy(() => import('./Screens/User/RefundPolicy/RefundPolicy'));
const AffiliateAuthScreens = lazy(() => import('./Screens/Affiliate/Auth/index'));
const AffiliateProfile = lazy(() => import('./Screens/Affiliate/Profile/AffiliateProfile'));
const BoostProfilePayment = lazy(() => import('./Screens/User/BoostProfilePayment/BoostProfilePayment'));
const AppLayout = lazy(() => import('./Layout/AppLayout'));

export default function App() {
  const { user, role = 'guest' } = useUserStore();
  const isAuthenticated = !!user; // Checks if user is logged in

  return (
    <TranslateProvider>
      <TranslateBanner />
      <Suspense fallback={<SkeletonLoader />}>
        <Router basename="/">
          <Routes>
            {/* Public Routes */}
            <Route
              element={
                <PublicRoutes
                  isAuthenticated={isAuthenticated}
                  redirectTo={role === 'admin' ? (isAuthenticated ? '/admin' : '/admin/login') : role === 'affiliate' ? (isAuthenticated ? '/affiliate' : '/affiliate/login') : isAuthenticated ? '/home' : '/login'}
                />
              }
            >
              <Route path="how-to-use" element={<HowToUse />} />
              {/* Home page accessible to both guests and authenticated users */}
              <Route element={<UserLayout variant="home-header" />}>
                <Route path="/" element={<Home />} />
                <Route path="faqs" element={<Faqs />} />
                <Route path="games" element={<Games />} />
                <Route path="games/tic-tac-toe" element={<TicTacToe />} />
                <Route path="games/emoji-play" element={<EmojiPlay />} />
                <Route path="games/this-or-that" element={<ThisOrThat />} />
                <Route path="watch-together/:roomId" element={<WatchTogether />} />
                <Route path="about-us" element={<AboutUs />} />
                <Route path="help-center" element={<HelpCenter />} />
                <Route path="settings" element={<Settings />} />
                <Route path="contact-us" element={<ContactUs />} />
                <Route path="privacy-policy" element={<PrivacyPolicy />} />
                <Route path="terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="shipping-policy" element={<ShippingPolicy />} />
                <Route path="refund-policy" element={<RefundPolicy />} />
              </Route>
              <Route element={<UserLayout />}>
                <Route path="login" element={<UserAuthScreens screen="login" />} />
                <Route path="forget-id" element={<UserAuthScreens screen="forget-id" />} />
                <Route path="forget-id2" element={<UserAuthScreens screen="forget-id2" />} />
                <Route path="forget-password" element={<UserAuthScreens screen="forget-password" />} />
                <Route path="forget-password2" element={<UserAuthScreens screen="forget-password2" />} />
                <Route path="forget-password3" element={<UserAuthScreens screen="forget-password3" />} />
                <Route path="signup" element={<UserAuthScreens screen="signup" />} />
              </Route>

              {/* Admin auth routes - loaded together */}
              <Route path="admin/login" element={<AdminAuthScreens screen="login" />} />
              <Route path="admin/forget-password" element={<AdminAuthScreens screen="forget-password" />} />
              <Route path="admin/forget-password2" element={<AdminAuthScreens screen="forget-password2" />} />
              <Route path="admin/forget-password3" element={<AdminAuthScreens screen="forget-password3" />} />
              {/* Affiliate auth routes - loaded together */}
              <Route path="affiliate/login" element={<AffiliateAuthScreens screen="login" />} />
              <Route path="affiliate/forget-password" element={<AffiliateAuthScreens screen="forget-password" />} />
              <Route path="affiliate/forget-password2" element={<AffiliateAuthScreens screen="forget-password2" />} />
              <Route path="affiliate/forget-password3" element={<AffiliateAuthScreens screen="forget-password3" />} />
            </Route>

            {/* Authenticated Routes Admin */}
            <Route element={isAuthenticated ? <AppLayout /> : <Navigate to="/login" />}>
              <Route path="admin/profile/admin-profile" element={<AdminProfile />} />
              {/* Admin Routes - use the grouped component for admin routes */}
              {role === 'admin' && (
                <>
                  <Route path="admin/*" element={<AdminScreens />} />
                  <Route path="/" element={<Navigate to="/admin/dashboard" />} />
                </>
              )}
            </Route>

            {/* Authenticated Routes Affiliate */}
            <Route element={isAuthenticated ? <AppLayout /> : <Navigate to="/affiliate/login" />}>
              <Route path="affiliate/profile" element={<AffiliateProfile />} />
              {/* Affiliate Routes - use the grouped component for affiliate routes */}
              {role === 'affiliate' && (
                <>
                  <Route path="affiliate/*" element={<AffiliateRoutes />} />
                  <Route path="/" element={<Navigate to="/affiliate/dashboard" />} />
                </>
              )}
            </Route>

            {/* Authenticated Routes User - only for protected routes, not home */}
            <Route element={isAuthenticated ? <UserLayout variant="home-header" /> : <Navigate to="/login" />}>
              <Route path="preferences" element={<Preferences />} />
              <Route path="profile" element={<Profile />} />
              <Route path="home" element={<InteractWithHappiness />} />
              <Route path="user/profile/:id" element={<UserProfile />} />
              <Route path="blocked-users" element={<BlockedUsers />} />
              <Route path="likes" element={<Likes />} />
              <Route path="subscription-plans" element={<SubscriptionPlans />} />
              <Route path="subscription-logs" element={<SubscriptionLogs />} />
              <Route path="refer-a-friend" element={<ReferAFriend />} />
              <Route path="affiliate" element={<AffiliateProgram />} />
              <Route path="coin-store" element={<CoinStore />} />
              <Route path="gift-store" element={<GiftStore />} />
              <Route path="chat" element={<Chat />} />
              <Route path="auto-reply" element={<AutoReply />} />
              <Route path="chat/:chatId/reminders" element={<Reminders />} />
              <Route path="edit-profile" element={<EditProfile />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="preferences" element={<Preferences />} />
              <Route path="boost-profile-payment" element={<BoostProfilePayment />} />
            </Route>

            {/* Optional: Catch all for 404 */}
            <Route path="*" element={isAuthenticated ? role === 'admin' ? <Navigate to="/admin" /> : <Navigate to="/" /> : <Navigate to="/login" />} />
          </Routes>
        </Router>
        <Toast />
        <DownloadAppSticky />
      </Suspense>
    </TranslateProvider>
  );
}